import { useLocalStorage } from '@vueuse/core'

import type { Customer } from '@brighterly/lib-core-types/customer/interfaces'
import type { DisciplineStore } from '@stores/discipline/interfaces'
import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import type { DisciplineInfo, DisciplineInKid } from '@brighterly/lib-core-types/discipline/interfaces'
import type { TutorContact } from '@brighterly/lib-core-types/tutor-contact/interfaces'

import { useRawDiscipline } from '@hooks/discipline/index.hook'

import { isFullArray } from '@helpers/array'

import { hasActiveDiscipline } from './getters'

const STORAGE_KEY = 'active_discipline'

const persist = (state: DisciplineStore): void => {
  const ls = useLocalStorage<DisciplineInKid | null>(STORAGE_KEY, null, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => (v ? JSON.stringify(v) : ''),
    },
  })
  const discipline = toRaw(state.activeDiscipline) as DisciplineInKid | null

  ls.value = discipline ? { ...discipline } : null
}

const setActiveDiscipline = (state: DisciplineStore, discipline: DisciplineInKid | null): void => {
  state.activeDiscipline = toRaw(discipline)
  persist(state)
}

const clear = (state: DisciplineStore): void => {
  state.activeDiscipline = null
  persist(state)
}

export const actions = {
  hydrateFromStorage (this: DisciplineStore): void {
    if (this._hydrated) return

    const ls = useLocalStorage<DisciplineInKid | null>(STORAGE_KEY, null, {
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => (v ? JSON.stringify(v) : ''),
      },
    })

    this.activeDiscipline = ls.value ? { ...ls.value } : null
    this._hydrated = true
  },
  persist (this: DisciplineStore): void {
    return persist(this)
  },
  setActiveDiscipline (this: DisciplineStore, discipline: DisciplineInKid | null): void {
    return setActiveDiscipline(this, discipline)
  },
  clear (this: DisciplineStore): void {
    return clear(this)
  },
  initFromKidInCustomer (this: DisciplineStore, customer: Customer | null, kidId: number): void {
    if (!isFullArray(customer?.kids)) {
      clear(this)
      return
    }

    if (hasActiveDiscipline(this)) return

    const kid = customer!.kids.find(kid => kid.id === kidId)!

    const discipline = kid?.disciplines?.[0] || null

    if (discipline) {
      this.activeDiscipline = discipline
      persist(this)
    }
  },
  reconcileWithKidInCustomer (this: DisciplineStore, customer: Customer | null, kidId: number): void {
    if (!isFullArray(customer?.kids)) {
      clear(this)
      return
    }

    const kid = customer!.kids.find(kid => kid.id === kidId)!

    if (!hasActiveDiscipline(this)) {
      setActiveDiscipline(this, kid.disciplines?.[0] || null)
      return
    }

    const disciplines = kid.disciplines.map(disciplineInKid => disciplineInKid.code)

    const exists = disciplines.some(discipline => discipline === this.activeDiscipline?.code)

    if (!exists) {
      setActiveDiscipline(this, kid.disciplines?.[0] || null)
    }
  },
  setMathLanding (this: DisciplineStore, payload: DisciplineStore['mathLanding']): void {
    this.mathLanding = payload
  },
  setElaLanding (this: DisciplineStore, payload: DisciplineStore['elaLanding']): void {
    this.elaLanding = payload
  },
  setDisciplineInfo (this: DisciplineStore, payload: DisciplineInfo | null, discipline?: Discipline): void {
    if (hasActiveDiscipline(this)) {
      const {
        isDisciplineMath,
        isDisciplineEla,
      } = useRawDiscipline({ discipline: discipline || this.activeDiscipline!.code })

      if (isDisciplineMath) {
        this.disciplineInfoMath = payload
      }
      if (isDisciplineEla) {
        this.disciplineInfoEla = payload
      }
    }
  },
  setDisciplineNewFiles (this: DisciplineStore, payload: number): void {
    const {
      isDisciplineMath,
      isDisciplineEla,
    } = useRawDiscipline({ discipline: this.activeDiscipline!.code })

    if (isDisciplineMath && this.disciplineInfoMath) {
      this.disciplineInfoMath.files_new = payload
    }

    if (isDisciplineEla && this.disciplineInfoEla) {
      this.disciplineInfoEla.files_new = payload
    }
  },
  setTutorContact (this: DisciplineStore, payload: TutorContact): void {
    const {
      isDisciplineMath,
      isDisciplineEla,
    } = useRawDiscipline({ discipline: this.activeDiscipline!.code })

    if (isDisciplineMath && this.disciplineInfoMath) {
      this.disciplineInfoMath.tutor_contact = payload
    }

    if (isDisciplineEla && this.disciplineInfoEla) {
      this.disciplineInfoEla.tutor_contact = payload
    }
  },
  setModalTutorProfile (this: DisciplineStore, payload: DisciplineStore['modalTutorProfile']): void {
    this.modalTutorProfile = payload
  },
}
